// Полоса «Нет связи» под шапкой листа (FR-040б; contracts/ui.md, «Сеанс и связь»; ux.md,
// «Нет связи»): цвет color-warn-soft, «Повторить» перечитывает данные экрана. Каждое
// несохранённое изменение даёт свой тост-ошибку с «Повторить»; тостов об успехе без связи нет
// (AppToasts, app/toasts.tsx). Связь вернулась сама — полоса исчезает.
// Перезапрос при возврате связи — фоновый (X-KP-Background: 1): его никто не просил, сеанс он
// не продлевает (FR-008б). «Повторить» — действие человека, его запросы обычные.
//
// 015 (ux CHK015): страница со своей полосой «Нет связи» (таблица протокола — полоса листа с числом
// неотправленных правок) отмечается useOwnOfflineStrip — общая полоса оболочки тогда не видна,
// второй полосы с тем же текстом нет. Перезапрос при возврате связи остаётся.
import { Button, Icon } from '@kp/ui';
import { WifiOff } from '@kp/ui/icons';
import { onlineManager, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { useOnline } from '../app/connection.ts';
import { refetchActive } from '../app/query-client.ts';

export const OFFLINE_BANNER_TEXT = 'Нет связи. Изменения сохранятся, когда связь вернётся';

/** Сколько смонтированных страниц показывают свою полосу «Нет связи». */
let ownStrips = 0;
const stripListeners = new Set<() => void>();

function changeOwnStrips(delta: number): void {
  ownStrips += delta;
  for (const listener of stripListeners) listener();
}

function subscribeOwnStrips(listener: () => void): () => void {
  stripListeners.add(listener);
  return () => stripListeners.delete(listener);
}

/**
 * Страница показывает свою полосу «Нет связи» (ux CHK015): пока она смонтирована, полоса оболочки
 * скрыта.
 */
export function useOwnOfflineStrip(): void {
  useEffect(() => {
    changeOwnStrips(1);
    return () => changeOwnStrips(-1);
  }, []);
}

export function OfflineBanner() {
  const online = useOnline();
  const ownStrip = useSyncExternalStore(
    subscribeOwnStrips,
    () => ownStrips > 0,
    () => false,
  );
  const queryClient = useQueryClient();
  const [retrying, setRetrying] = useState(false);

  const retry = useCallback(
    async (background: boolean) => {
      setRetrying(true);
      try {
        // Первый же ответ сервера возвращает связь (клиент API).
        await refetchActive(queryClient, { background });
      } finally {
        setRetrying(false);
      }
    },
    [queryClient],
  );

  // Браузер сообщил, что сеть вернулась, — проверяем сразу. Подписка — на onlineManager
  // TanStack: к его уведомлению запросы уже не стоят на паузе и уходят сразу, внутри
  // inBackground.
  useEffect(
    () =>
      onlineManager.subscribe((isOnline) => {
        if (isOnline) void retry(true);
      }),
    [retry],
  );

  return (
    <div className="kp-offline-slot" role="status">
      {online || ownStrip ? null : (
        <div className="kp-offline">
          <Icon icon={WifiOff} />
          <span className="kp-offline-text">{OFFLINE_BANNER_TEXT}</span>
          <Button size="sm" loading={retrying} onClick={() => void retry(false)}>
            Повторить
          </Button>
        </div>
      )}
    </div>
  );
}
